import { Controller } from "@hotwired/stimulus"

// Order form with a fixed pool of item rows; rows beyond the first start hidden and are revealed
// on demand. Each row's colour options come from the chosen product's palette. The row controls
// have no name attributes: the order reaches Netlify only as the generated `items` line.
// `subject` sets the notification email subject; `summary` repeats it as a textarea because
// Netlify uses the first textarea as the submission's title in the dashboard.
export default class extends Controller {
  static targets = ["item", "add", "total", "totalBox", "palette", "subject", "summary", "customer", "items"]
  static values = { locale: String }

  connect() {
    if (!this.hasPaletteTarget) return // pages without the order form

    this.palette = JSON.parse(this.paletteTarget.textContent)
    this.lang = document.documentElement.lang
    this.refresh()
  }

  add() {
    const row = this.hiddenItems[0]
    if (row) this.show(row)
  }

  remove(event) {
    this.hide(event.target.closest("[data-order-target='item']"))
  }

  // Called from a product card: fill the first empty visible row, or reveal a new one
  choose({ params: { slug } }) {
    const row = this.visibleItems.find(r => !this.product(r).value) || this.hiddenItems[0]
    if (!row) return

    this.show(row)
    const option = this.product(row).querySelector(`option[data-slug="${slug}"]`)
    if (option) {
      option.selected = true
      this.syncColours(row)
    }
    this.update()
  }

  productChanged(event) {
    this.syncColours(event.target.closest("[data-order-target='item']"))
  }

  colourChanged(event) {
    this.syncSwatch(event.target.closest("[data-order-target='item']"))
  }

  update() {
    const total = this.visibleItems.reduce((sum, row) => {
      const option = this.product(row).selectedOptions[0]
      return sum + Number(option?.dataset.price || 0) * this.quantity(row)
    }, 0)

    const formatted = new Intl.NumberFormat(this.localeValue, { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(total)
    this.totalTarget.textContent = formatted
    this.totalBoxTarget.classList.toggle("hidden", total === 0)

    const label = total > 0 ? formatted : null
    this.subjectTarget.value = this.subject(label)
    this.summaryTarget.value = this.subjectTarget.value
    this.itemsTarget.value = this.itemsList(label)
  }

  // Unique email subject per order so notifications don't thread together
  subject(total) {
    const parts = [`Knitty Kitty order (${this.lang})`]
    if (this.customerTarget.value.trim()) parts.push(this.customerTarget.value.trim())
    if (total) parts.push(total)
    return parts.join(" · ")
  }

  // Everything Kitty needs on one line, e.g. "1× Heart Earrings (Blue), 2× Mochi Cat (Green) · €10"
  itemsList(total) {
    const items = this.visibleItems
      .filter(row => this.product(row).value)
      .map(row => {
        const colour = this.colour(row).value
        return `${this.quantity(row)}× ${this.product(row).value}${colour ? ` (${colour})` : ""}`
      })
    return items.length ? `${items.join(", ")}${total ? ` · ${total}` : ""}` : ""
  }

  show(row) {
    row.classList.remove("hidden")
    this.product(row).required = true
    this.refresh()
  }

  hide(row) {
    row.classList.add("hidden")
    this.product(row).required = false
    this.product(row).value = ""
    row.querySelector("input[type='number']").value = 1
    this.syncColours(row)
    this.refresh()
  }

  refresh() {
    this.addTarget.classList.toggle("hidden", this.hiddenItems.length === 0)
    this.update()
  }

  // Rebuild the colour dropdown for the row's current product
  syncColours(row) {
    const colour = this.colour(row)
    const product = this.product(row).selectedOptions[0]
    const keys = product?.dataset.colours ? product.dataset.colours.split(",") : []
    const placeholder = colour.dataset[keys.length ? "placeholder" : "first"]

    colour.replaceChildren(new Option(placeholder, "", true, true))
    colour.options[0].disabled = true
    keys.forEach(key => {
      const option = new Option(this.palette[key].name[this.lang], this.palette[key].name[this.lang])
      option.dataset.hex = this.palette[key].hex
      colour.add(option)
    })
    colour.disabled = keys.length === 0
    this.syncSwatch(row)
  }

  syncSwatch(row) {
    const swatch = row.querySelector("[data-order-target='swatch']")
    const hex = this.colour(row).selectedOptions[0]?.dataset.hex
    swatch.style.backgroundColor = hex || ""
    swatch.classList.toggle("hidden", !hex)
    this.colour(row).classList.toggle("pl-9", Boolean(hex))
  }

  product(row) {
    return row.querySelector("[data-order-target='product']")
  }

  colour(row) {
    return row.querySelector("[data-order-target='colour']")
  }

  quantity(row) {
    return Number(row.querySelector("input[type='number']").value || 1)
  }

  get visibleItems() {
    return this.itemTargets.filter(r => !r.classList.contains("hidden"))
  }

  get hiddenItems() {
    return this.itemTargets.filter(r => r.classList.contains("hidden"))
  }
}
